/**
 * AllocationCard — interactive donut + IPS target-vs-actual breakdown.
 *
 * Phase 3 Task 3.3 (REQ-DASH-002, 2026-05-17): rewired off the Anna
 * ALLOCATION fixture onto `useDashboardAllocation(trustId)`. Renders the
 * actual portfolio split as a donut, with a delta row per asset class
 * showing target weight, actual weight, and rebalance status (within-band /
 * rebalance-buy / rebalance-sell). When the trust has no IPS set yet, the
 * card surfaces the actual-only breakdown so the chair can see the holdings
 * and prompt the admin to set targets.
 */

import { useState } from 'react';
import { DonutChart, type DonutSlice } from '../charts/donut-chart';
import {
  assetClassColorVar,
  assetClassLabel,
  formatBps,
  formatZarCents,
  useDashboardAllocation,
  type AllocationDeltaRow,
} from '../../lib/api';

export interface AllocationCardProps {
  trustId: string | undefined;
}

function statusLabel(s: AllocationDeltaRow['status']): string {
  switch (s) {
    case 'within-band':
      return 'Within band';
    case 'rebalance-buy':
      return 'Rebalance - buy';
    case 'rebalance-sell':
      return 'Rebalance - sell';
  }
}

function statusColor(s: AllocationDeltaRow['status']): string {
  switch (s) {
    case 'within-band':
      return 'var(--primary-700)';
    case 'rebalance-buy':
      return 'var(--accent-600)';
    case 'rebalance-sell':
      return 'var(--negative, #b94c50)';
  }
}

export function AllocationCard({ trustId }: AllocationCardProps) {
  const { data, isLoading, isError } = useDashboardAllocation(trustId);
  const [hovered, setHovered] = useState<string | null>(null);

  if (!trustId) {
    return (
      <div className="card">
        <div className="card-head">
          <div>
            <div className="eyebrow">Allocation</div>
            <h2 className="card-title">How the trust is held</h2>
          </div>
        </div>
        <p style={{ color: 'var(--ink-3)', fontSize: 13, padding: '8px 0', lineHeight: 1.6 }}>
          Allocation appears once a trust is onboarded. The donut and IPS
          target-vs-actual lines render automatically when the first
          holdings lodge.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="card" aria-busy="true" aria-live="polite">
        <div className="card-head">
          <div>
            <div className="eyebrow">Allocation</div>
            <h2 className="card-title">How the trust is held</h2>
          </div>
        </div>
        <div
          data-testid="allocation-skeleton"
          style={{ minHeight: 220, color: 'var(--ink-3)', fontSize: 13, padding: 24 }}
        >
          Loading allocation breakdown&hellip;
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="card" role="alert">
        <div className="card-head">
          <div>
            <div className="eyebrow">Allocation</div>
            <h2 className="card-title">How the trust is held</h2>
          </div>
        </div>
        <p style={{ color: 'var(--ink-3)', fontSize: 13, padding: '8px 0', lineHeight: 1.6 }}>
          Allocation isn&rsquo;t available for this trust yet. Once holdings
          are recorded, this card will refresh on the next nightly snapshot.
        </p>
      </div>
    );
  }

  const rows = data?.rows ?? [];

  if (rows.length === 0) {
    return (
      <div className="card">
        <div className="card-head">
          <div>
            <div className="eyebrow">Allocation</div>
            <h2 className="card-title">How the trust is held</h2>
          </div>
        </div>
        <p
          role="status"
          style={{ color: 'var(--ink-3)', fontSize: 13, padding: '8px 0', lineHeight: 1.6 }}
        >
          No holdings recorded yet. Lodge a holding to see the
          allocation split against the trust&rsquo;s IPS targets.
        </p>
      </div>
    );
  }

  const slices: DonutSlice[] = rows.map((r) => ({
    id: r.assetClass,
    pct: r.actualBps / 100,
    color: assetClassColorVar(r.assetClass),
  }));

  const focusRow = hovered
    ? rows.find((r) => r.assetClass === hovered) ?? rows[0]!
    : rows[0]!;

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <div className="eyebrow">Allocation</div>
          <h2 className="card-title">How the trust is held</h2>
        </div>
        <span className="card-meta">
          {data?.ipsAsOf
            ? `IPS as of ${new Date(data.ipsAsOf).toLocaleDateString('en-ZA', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}`
            : 'No IPS targets set'}
        </span>
      </div>

      <div className="alloc-grid">
        <div className="donut-wrap">
          <DonutChart data={slices} hoveredId={hovered} onHover={setHovered} />
          {/* Two-line centre (design-handoff cohesion pass, 1a, 2026-09-15):
              "centre reads 43% over listed equity" — the design gate FAILED
              this screen for still drawing the retired three-line stack
              (an uppercase LARGEST/Hovered caption, the italic asset class,
              then the percentage and the money value on a third line). The
              caption and the money figure are gone; the percentage and the
              asset class name are what is left, in that order. */}
          <div className="donut-center">
            <div className="l3">{formatBps(focusRow.actualBps)}</div>
            <div className="l2">
              {assetClassLabel(focusRow.assetClass)}
            </div>
          </div>
        </div>

        <div className="alloc-list">
          {rows.map((r) => (
            <div
              key={r.assetClass}
              className="alloc-row"
              data-status={r.status}
              onMouseEnter={() => setHovered(r.assetClass)}
              onMouseLeave={() => setHovered(null)}
            >
              <div
                className="alloc-swatch"
                style={{ background: assetClassColorVar(r.assetClass) }}
              />
              <div className="alloc-name">
                {assetClassLabel(r.assetClass)}
                <span className="sub" style={{ color: statusColor(r.status) }}>
                  {statusLabel(r.status)}
                  {r.targetBps > 0 && (
                    <>
                      {' '}- target {formatBps(r.targetBps)} +/-{' '}
                      {formatBps(r.toleranceBps)}
                    </>
                  )}
                </span>
              </div>
              <div className="alloc-pct">{formatBps(r.actualBps)}</div>
              <div className="alloc-amt">
                {formatZarCents(r.actualMarketValueZar)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
